import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneRight, Sparkle, CheckCircle } from "@phosphor-icons/react";

export function DemoChat({ title, subtitle, greeting, suggestions, handoff, contextNote, live = false, agentId = "demo" }) {
    const [messages, setMessages] = useState([{ from: "ai", text: greeting }]);
    const [input, setInput] = useState("");
    const [typing, setTyping] = useState(false);
    const [qualified, setQualified] = useState(false);
    const [liveFailed, setLiveFailed] = useState(false);
    const asked = useRef(0);
    const bodyRef = useRef(null);
    const sessionId = useRef(`${agentId}-${Math.random().toString(36).slice(2, 10)}`).current;

    useEffect(() => {
        bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, typing, qualified]);

    const answerFor = (text) => {
        const t = text.toLowerCase();
        const hit = suggestions.find((s) => {
            const q = s.q.toLowerCase();
            return t === q || q.split(/\W+/).filter((w) => w.length > 4 && t.includes(w)).length >= 2;
        });
        if (hit) return hit.a;
        return "That's a great question — in a live deployment I'd answer instantly from the business's own data. For this demonstration, may I ask a little more about what you're looking to achieve?";
    };

    const scriptedReply = (text) => {
        const isHandoff = asked.current >= 2 && handoff;
        setTyping(false);
        setMessages((m) => [...m, { from: "ai", text: isHandoff ? handoff : answerFor(text) }]);
    };

    const send = async (raw) => {
        const text = (raw ?? input).trim();
        if (!text || typing) return;
        setMessages((m) => [...m, { from: "visitor", text }]);
        setInput("");
        setTyping(true);
        asked.current += 1;
        if (asked.current >= 2) setTimeout(() => setQualified(true), 1500);
        if (!live || liveFailed) {
            setTimeout(() => scriptedReply(text), 900 + Math.random() * 700);
            return;
        }
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ agent_id: agentId, session_id: sessionId, message: text }),
            });
            if (!res.ok || !res.body) throw new Error("agent unavailable");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let acc = "";
            let appended = false;
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                acc += decoder.decode(value, { stream: true });
                if (acc.startsWith("AGENT_UNAVAILABLE")) throw new Error("agent unavailable");
                const current = acc;
                if (!appended) {
                    appended = true;
                    setTyping(false);
                    setMessages((m) => [...m, { from: "ai", text: current }]);
                } else {
                    setMessages((m) => [...m.slice(0, -1), { from: "ai", text: current }]);
                }
            }
            if (!acc.trim()) throw new Error("empty reply");
        } catch {
            setLiveFailed(true);
            setTyping(false);
            scriptedReply(text);
        }
    };

    return (
        <div className="flex h-full flex-col" data-testid="demo-chat">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                <div>
                    <p className="font-display text-sm font-bold tracking-wide text-white">{title}</p>
                    {subtitle && <p className="text-xs text-white/45">{subtitle}</p>}
                </div>
                <span className="flex items-center gap-1.5 border border-white/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50" data-testid="simulation-badge">
                    <Sparkle size={11} /> {live && !liveFailed ? "Live AI agent" : "Simulated demo"}
                </span>
            </div>
            <div ref={bodyRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-lenis-prevent>
                {messages.map((m, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${m.from === "visitor" ? "justify-end" : "justify-start"}`}
                    >
                        <div className={`max-w-[82%] px-4 py-3 text-sm leading-relaxed ${m.from === "visitor" ? "bg-white text-black" : "border border-white/12 bg-elev text-white/85"}`}>
                            {m.from === "ai" && <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white/35">AI · {title}</p>}
                            {m.text}
                        </div>
                    </motion.div>
                ))}
                {typing && (
                    <div className="flex justify-start">
                        <div className="flex items-center gap-1.5 border border-white/12 bg-elev px-4 py-3">
                            {[0, 1, 2].map((d) => (
                                <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-white/50"
                                    animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.18 }} />
                            ))}
                        </div>
                    </div>
                )}
                <AnimatePresence>
                    {qualified && (
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="border border-white/15 bg-black/60 p-4"
                            data-testid="lead-qualified-card"
                        >
                            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white">
                                <CheckCircle size={15} weight="fill" /> Lead qualified — CRM updated
                            </p>
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {["Enquiry", "Qualified", "CRM", "Appointment", "Follow-up"].map((s) => (
                                    <span key={s} className="border border-white/12 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-white/50">{s}</span>
                                ))}
                            </div>
                            <Link
                                to="/book"
                                data-testid="demo-book-button"
                                className="mt-4 block w-full bg-white py-3 text-center text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-white/85"
                            >
                                Book consultation
                            </Link>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <div className="border-t border-white/10 p-4">
                <div className="mb-3 flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                        <button
                            key={s.q}
                            data-testid={`suggestion-${s.q.slice(0, 18).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => send(s.q)}
                            className="border border-white/15 px-2.5 py-1.5 text-[11px] text-white/60 transition-colors duration-200 hover:border-white/40 hover:text-white"
                        >
                            {s.q}
                        </button>
                    ))}
                </div>
                <form
                    onSubmit={(e) => { e.preventDefault(); send(); }}
                    className="flex items-center gap-2"
                >
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        data-testid="demo-chat-input"
                        placeholder="Ask anything…"
                        className="h-11 flex-1 border border-white/15 bg-black px-4 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none"
                    />
                    <button type="submit" data-testid="demo-chat-send" aria-label="Send message"
                        className="flex h-11 w-11 items-center justify-center bg-white text-black transition-colors hover:bg-white/85">
                        <PaperPlaneRight size={18} weight="fill" />
                    </button>
                </form>
                {contextNote && <p className="mt-2 text-[10px] text-white/30">{contextNote}</p>}
            </div>
        </div>
    );
}
